import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import { videoService } from '@/lib/api/services/video'
import type {
  Video,
  CreateVideo,
  UpdateVideo,
  UpdateVideoStatus,
  VideoStats,
  PaginationParams,
  VideoFilterParams,
  ApiResponse,
  PaginatedResponse,
  VideoFilterResponse,
  VideoDashboardStats
} from '@/lib/api/types'

// Query keys
export const videoKeys = {
  all: ['videos'] as const,
  lists: () => [...videoKeys.all, 'list'] as const,
  list: (params: PaginationParams) => [...videoKeys.lists(), params] as const,
  filters: () => [...videoKeys.all, 'filter'] as const,
  filter: (params: PaginationParams, filters: VideoFilterParams) =>
    [...videoKeys.filters(), params, filters] as const,
  details: () => [...videoKeys.all, 'detail'] as const,
  detail: (id: string) => [...videoKeys.details(), id] as const,
  stats: () => [...videoKeys.all, 'stats'] as const,
  dashboardStats: () => [...videoKeys.all, 'dashboard-stats'] as const,
  search: (query: string, params: PaginationParams) => [...videoKeys.all, 'search', query, params] as const
}

// Hook to filter videos (admin) - returns nested folder/video structure
export function useFilterVideos(
  params: PaginationParams = { page: 1, size: 10 },
  filters: VideoFilterParams = {}
) {
  return useQuery<ApiResponse<VideoFilterResponse>, Error>({
    queryKey: videoKeys.filter(params, filters),
    queryFn: () => videoService.filterVideos(params, filters)
  })
}

// Hook to fetch a single video by ID
export function useVideo(videoId: string) {
  return useQuery<ApiResponse<Video>, Error>({
    queryKey: videoKeys.detail(videoId),
    queryFn: () => videoService.getVideoById(videoId),
    enabled: !!videoId
  })
}

// Hook to fetch video stats
export function useVideoStats() {
  return useQuery<ApiResponse<VideoStats>, Error>({
    queryKey: videoKeys.stats(),
    queryFn: () => videoService.getVideoStats()
  })
}

export function useVideoDashboardStats() {
  return useQuery<ApiResponse<VideoDashboardStats>, Error, VideoDashboardStats>({
    queryKey: videoKeys.dashboardStats(),
    queryFn: () => videoService.getVideoDashboardStats(),
    select: (response) => response.data,
    staleTime: 5 * 60 * 1000,
  })
}

// Hook to fetch published video count
export function usePublishedStats() {
  return useQuery<ApiResponse<number>, Error>({
    queryKey: [...videoKeys.stats(), 'published'],
    queryFn: () => videoService.getPublishedStats()
  })
}


export function useDraftStats() {
  return useQuery<ApiResponse<number>, Error>({
    queryKey: [...videoKeys.stats(), 'draft'],
    queryFn: () => videoService.getDraftStats()
  })
}


export function useCreateVideo() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<Video>, Error, CreateVideo>({
    mutationFn: (data) => videoService.createVideo(data),
    onSuccess: () => {

      queryClient.invalidateQueries({ queryKey: videoKeys.filters() })
      queryClient.invalidateQueries({ queryKey: videoKeys.stats() })
      queryClient.invalidateQueries({ queryKey: [...videoKeys.stats(), 'published'] })
      queryClient.invalidateQueries({ queryKey: [...videoKeys.stats(), 'draft'] })
    }
  })
}


export function useUpdateVideo() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<Video>,
    Error,
    { videoId: string; data: UpdateVideo }
  >({
    mutationFn: ({ videoId, data }) =>
      videoService.updateVideo(videoId, data),
    onSuccess: (_, { videoId }) => {
      queryClient.invalidateQueries({ queryKey: videoKeys.detail(videoId) })
      queryClient.invalidateQueries({ queryKey: videoKeys.filters() })
      queryClient.invalidateQueries({ queryKey: videoKeys.stats() })
      queryClient.invalidateQueries({ queryKey: [...videoKeys.stats(), 'published'] })
      queryClient.invalidateQueries({ queryKey: [...videoKeys.stats(), 'draft'] })
    }
  })
}

// Hook to delete a video
export function useDeleteVideo() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<null>, Error, string>({
    mutationFn: (videoId: string) => videoService.deleteVideo(videoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: videoKeys.filters() })
      queryClient.invalidateQueries({ queryKey: videoKeys.stats() })
    }
  })
}

// Hook to update video status
export function useUpdateVideoStatus() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<null>,
    Error,
    { videoId: string; data: UpdateVideoStatus }
  >({
    mutationFn: ({ videoId, data }) =>
      videoService.updateVideoStatus(videoId, data),
    onSuccess: (_, { videoId }) => {
      queryClient.invalidateQueries({ queryKey: videoKeys.detail(videoId) })
      queryClient.invalidateQueries({ queryKey: videoKeys.filters() })
      queryClient.invalidateQueries({ queryKey: videoKeys.stats() })
    }
  })
}

// Hook to search videos
export function useSearchVideos(
  params: PaginationParams = { page: 1, size: 10 },
  searchBy?: string,
  enabled: boolean = true
) {
  return useQuery<ApiResponse<PaginatedResponse<Video>>, Error>({
    queryKey: videoKeys.search(searchBy || '', params),
    queryFn: () => videoService.searchVideos(params, searchBy),
    enabled: enabled && !!searchBy && searchBy.length > 0,
    staleTime: 2 * 60 * 1000 // 2 minutes
  })
}

// Hook for infinite scroll videos
export function useInfiniteVideos(baseFilters: VideoFilterParams = {}) {
  return useInfiniteQuery<ApiResponse<PaginatedResponse<Video>>, Error, ApiResponse<PaginatedResponse<Video>>, readonly unknown[], number>({
    queryKey: [...videoKeys.filters(), 'infinite', baseFilters],
    queryFn: ({ pageParam }) =>
      videoService.filterVideos({ page: pageParam as number, size: 10 }, baseFilters),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.success || !lastPage.data) return undefined
      const currentPage = allPages.length
      const totalPages = lastPage.data.totalPage
      return currentPage < totalPages ? currentPage + 1 : undefined
    },
    staleTime: 2 * 60 * 1000
  })
}

// Hook to fetch home page highlights
export function useHomePageHighlights(type: string = 'HOME_PAGE_IS_KEY_HIGHLIGHT') {
  return useQuery<ApiResponse<Video[]>, Error>({
    queryKey: ['videos', 'highlights', 'home', type],
    queryFn: () => videoService.getHomePageHighlights(type)
  })
}

// Hook to update home page highlights
export function useUpdateHomePageHighlights() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<Video[]>, Error, { videoUploadIds: string[], type?: string }>({
    mutationFn: ({ videoUploadIds, type }) => videoService.updateHomePageHighlights(videoUploadIds, type),
    onSuccess: (_, { type }) => {
      queryClient.invalidateQueries({ queryKey: ['videos', 'highlights', 'home', type || 'HOME_PAGE_IS_KEY_HIGHLIGHT'] })
    }
  })
}
